import { useState } from 'react'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { Card } from '../ui/card'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { LoadingSpinner } from '../ui/loading-states'
import { QueryError } from '../ui/error-state'
import { Pagination } from '../ui/pagination'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { DeliveryStats } from './DeliveryStats'
import { useNotifications, useSendNotification, useDeleteNotification } from '../../hooks/useNotifications'
import {
  Notification,
  NotificationFilters,
  getNotificationTypeLabel,
  getNotificationPriorityLabel,
  getNotificationPriorityColor,
} from '@tco/shared-types'
import { Bell, Send, Trash2, BarChart3, Clock } from 'lucide-react'

interface NotificationsListProps {
  filters?: NotificationFilters
  onNotificationClick?: (notification: Notification) => void
}

export function NotificationsList({ filters = {}, onNotificationClick }: NotificationsListProps) {
  const [page, setPage] = useState(filters.page || 1)
  const [limit] = useState(filters.limit || 20)
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null)
  const [showStatsDialog, setShowStatsDialog] = useState(false)

  const { data, isLoading, isError, error, refetch } = useNotifications({
    ...filters,
    page,
    limit,
  })

  const sendMutation = useSendNotification()
  const deleteMutation = useDeleteNotification()

  const handleSend = async (id: string) => {
    if (!confirm('Отправить это уведомление?')) return

    try {
      await sendMutation.mutateAsync(id)
      alert('Уведомление отправлено!')
      refetch()
    } catch (err) {
      alert('Ошибка отправки: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'))
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить это уведомление? Это действие нельзя отменить.')) return

    try {
      await deleteMutation.mutateAsync(id)
      alert('Уведомление удалено')
      refetch()
    } catch (err) {
      alert('Ошибка удаления: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'))
    }
  }

  const handleShowStats = (id: string) => {
    setSelectedNotificationId(id)
    setShowStatsDialog(true)
  }

  if (isLoading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-6">
        <QueryError error={error} refetch={refetch} />
      </Card>
    )
  }

  if (!data || data.data.length === 0) {
    return (
      <Card className="p-8">
        <div className="text-center">
          <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-lg font-medium">Уведомления не найдены</p>
          <p className="text-sm text-muted-foreground mt-1">
            Создайте первое уведомление, чтобы начать
          </p>
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Тип</TableHead>
                <TableHead>Заголовок</TableHead>
                <TableHead>Получатель</TableHead>
                <TableHead>Приоритет</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Создано</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.data.map((notification) => (
                <TableRow
                  key={notification.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onNotificationClick?.(notification)}
                >
                  <TableCell>
                    <Badge variant="outline">
                      {getNotificationTypeLabel(notification.type)}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <div>
                      <p className="font-medium">{notification.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {notification.body}
                      </p>
                    </div>
                  </TableCell>

                  <TableCell>
                    {notification.is_broadcast ? (
                      <Badge>Всем сотрудникам</Badge>
                    ) : notification.recipient_employee ? (
                      <span className="text-sm">
                        {notification.recipient_employee.first_name}{' '}
                        {notification.recipient_employee.last_name}
                      </span>
                    ) : notification.recipient_user ? (
                      <span className="text-sm">{notification.recipient_user.email}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    <Badge className={getNotificationPriorityColor(notification.priority)}>
                      {getNotificationPriorityLabel(notification.priority)}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    {notification.is_sent ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <Send className="h-3 w-3" />
                        <span className="text-xs">Отправлено</span>
                      </div>
                    ) : notification.scheduled_for ? (
                      <div className="flex items-center gap-1 text-blue-600">
                        <Clock className="h-3 w-3" />
                        <span className="text-xs">Запланировано</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Черновик</span>
                    )}
                  </TableCell>

                  <TableCell>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(notification.created_at), 'dd MMM yyyy, HH:mm', {
                        locale: ru,
                      })}
                    </span>
                  </TableCell>

                  <TableCell className="text-right">
                    <div
                      className="flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {notification.is_sent && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleShowStats(notification.id)}
                          disabled={sendMutation.isPending || deleteMutation.isPending}
                          title="Статистика"
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Button>
                      )}

                      {!notification.is_sent && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSend(notification.id)}
                          disabled={sendMutation.isPending || deleteMutation.isPending}
                          title="Отправить"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      )}

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(notification.id)}
                        disabled={sendMutation.isPending || deleteMutation.isPending}
                        title="Удалить"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {data.totalPages > 1 && (
          <div className="border-t p-4">
            <Pagination
              currentPage={page}
              totalPages={data.totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </Card>

      {/* Stats Dialog */}
      <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Статистика доставки</DialogTitle>
          </DialogHeader>
          {selectedNotificationId && (
            <DeliveryStats notificationId={selectedNotificationId} />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
